import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { getAuthBearerToken, PROACTIVE_TOKEN_REFRESH_MS } from '@/lib/authToken';
import { fetchEffectiveDashboardRole } from '@/lib/dashboardRoleResolution';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [dashboardRole, setDashboardRole] = useState(undefined);
  const [dashboardRoleLoading, setDashboardRoleLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setDashboardRoleLoading(true);
        let nextToken = null;
        try {
          nextToken = await firebaseUser.getIdToken(false);
        } catch {
          nextToken = localStorage.getItem('authToken');
        }
        setToken(nextToken);
        setUser(firebaseUser);
        if (nextToken) localStorage.setItem('authToken', nextToken);
      } else {
        setUser(null);
        setToken(null);
        setDashboardRole(undefined);
        setDashboardRoleLoading(false);
        localStorage.removeItem('authToken');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Mantener JWT alineado con Firebase (renovación automática ~1h).
  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) return;
      try {
        const next = await firebaseUser.getIdToken();
        if (!next) return;
        setToken((prev) => (prev === next ? prev : next));
        localStorage.setItem('authToken', next);
      } catch {
        /* ignore */
      }
    });
    return unsub;
  }, []);

  // Al volver a la app (PWA iOS): refrescar ID token mientras Firebase user exista.
  // No debe re-bootstrapear el rol: un setToken nuevo desmontaba el panel y borraba formularios.
  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!auth.currentUser) return;
      void (async () => {
        const next = await getAuthBearerToken(true);
        if (!next) return;
        setToken((prev) => (prev === next ? prev : next));
        try {
          localStorage.setItem('authToken', next);
        } catch {
          /* ignore */
        }
      })();
    };

    document.addEventListener('visibilitychange', refreshIfVisible);

    const intervalId = window.setInterval(refreshIfVisible, PROACTIVE_TOKEN_REFRESH_MS);

    return () => {
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.clearInterval(intervalId);
    };
  }, []);

  // Rol del panel: solo cuando cambia el usuario autenticado, no en cada refresh de JWT.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    setDashboardRoleLoading(true);

    (async () => {
      const role = await fetchEffectiveDashboardRole();
      if (!cancelled) {
        setDashboardRole(role);
        setDashboardRoleLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const register = (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    return signOut(auth);
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    dashboardRole,
    dashboardRoleLoading,
    setDashboardRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};